import { useState, useCallback, useRef, useEffect } from "react";
import { IGroup } from "../../common/types/groupTypes";
import { useDispatch, useSelector } from "react-redux";
import useNotification from "../notification/useNotification";
import { selector as everbridgeTokenApi } from "../configureEverbridgeTokenModal/configureEverbridgeTokenSlice";
import { fetchEverbridgeGroupData, selector as getEverbridgeGroup } from "../../pages/integrations/everbridge/listEverbridgeGroupSlice";
import { selector as groupApi } from "../../pages/deviceManagement/groupSlice";
import { mapEverbridgeGroupData } from "./mapEverbridgeGroupsSlice";
import { isSuccessResponse } from "../../common/utils";

interface IGroupData {
    everbridgeGroupId: string;
    everbridgeGroupName: string;
    groupId: string;
    groupName: string;
    _id: string;
}

export const useMapEverbridgeGroupModal = () => {
    const dispatch: any = useDispatch();
    const [showModel, setShowModel] = useState<boolean>(false);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const isTokenAdded: boolean = Boolean(useSelector(everbridgeTokenApi)?.userId);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const groupsDetails: IGroup[] = useSelector(groupApi).groups;
    const everbridgeGroupDetails = useSelector(getEverbridgeGroup).everbridgeGroup;
    const userId = everbridgeGroupDetails?.userId;
    let groups: string[] | any;
    let everbridgeGroupData: IGroupData[] = [];

    if (everbridgeGroupDetails && everbridgeGroupDetails.groups) {
        everbridgeGroupData = everbridgeGroupDetails.groups;
    }

    useEffect(() => {
        if (everbridgeGroupDetails && groupsDetails) {
            const initialAssignments = everbridgeGroupDetails.groups.reduce((acc, everbridgeGroup) => {
                acc[everbridgeGroup.everbridgeGroupId] = everbridgeGroup.groupName || null;
                return acc;
            }, {});
            setAssignments(initialAssignments);
        }
    }, [everbridgeGroupDetails, groupsDetails]);

    useEffect(() => {
        if (groupsDetails) {
            groups = groupsDetails.map((item) => item.displayName);
            setAvailableGroups(groups);
        }
    }, [groupsDetails]);

    const key: Record<string, string | null> = everbridgeGroupData.reduce(
        (everbridgeGroupAccumulator, currentEverbridgeGroup) => {
            if (currentEverbridgeGroup.groupId !== "" && currentEverbridgeGroup.groupName !== "") {
                everbridgeGroupAccumulator[currentEverbridgeGroup.everbridgeGroupId] = currentEverbridgeGroup.groupName;
            } else {
                everbridgeGroupAccumulator[currentEverbridgeGroup.everbridgeGroupId] = null;
            }
            return everbridgeGroupAccumulator;
        },
        {}
    );

    const [assignments, setAssignments] =
        useState<Record<string, string | null>>(key);
    const [availableGroups, setAvailableGroups] = useState<string[] | null>(
        groups
    );

    const handleClose = useCallback(() => {
        setShowModel(false);
    }, []);

    const handleShow = useCallback(() => {
        const initialAssignments = everbridgeGroupData.reduce((acc, everbridgeGroup) => {
            acc[everbridgeGroup.everbridgeGroupId] = everbridgeGroup.groupName || null;
            return acc;
        }, {});
        setAssignments(initialAssignments);
        setShowModel(true);
    }, [everbridgeGroupData]);

    const { showSuccessNotification, showWarningNotification } =
        useNotification();

    const handleMapEverbridgeGroups = useCallback(() => {
        const mappedSitesDetails = everbridgeGroupData.map((everbridgeGroup) => {
            const assignedGroupName = assignments[everbridgeGroup.everbridgeGroupId] || "";
            const assignedGroup = groupsDetails.find(
                (group) => group.displayName === assignedGroupName
            );
            return {
                everbridgeGroupId: everbridgeGroup.everbridgeGroupId,
                everbridgeGroupName: everbridgeGroup.everbridgeGroupName,
                groupId: assignedGroup ? assignedGroup.orgId : "",
                groupName: assignedGroupName,
            };
        });

        const payload = {
            userId: userId,
            everbridgeGroups: { groups: mappedSitesDetails },
        };
        dispatch(mapEverbridgeGroupData(payload)).then((response) => {
            if (isSuccessResponse(response.payload)) {
                dispatch(fetchEverbridgeGroupData());
                showSuccessNotification(response.payload.message);
                setShowModel(false);
            } else {
                showWarningNotification(response.payload.response.data.message);
            }
        });
    }, [
        everbridgeGroupData,
        assignments,
        groupsDetails,
        userId,
        dispatch,
        showSuccessNotification,
        showWarningNotification,
    ]);

    const handleSelectGroup = (everbridgeGroupId: string, groupName: string): void => {
        const updatedAssignments: Record<string, string | null> = Object.keys(
            assignments
        ).reduce((assignmentAccumulator, currentAssignmentKey) => {
            if (assignments[currentAssignmentKey] === groupName) {
                assignmentAccumulator[currentAssignmentKey] = null;
            } else {
                assignmentAccumulator[currentAssignmentKey] =
                    assignments[currentAssignmentKey];
            }
            return assignmentAccumulator;
        }, {});

        setAssignments({
            ...updatedAssignments,
            [everbridgeGroupId]: groupName,
        });
    };

    return {
        showModel,
        handleShow,
        handleClose,
        showTooltip,
        setShowTooltip,
        tooltipRef,
        isTokenAdded,
        handleSelectGroup,
        assignments,
        setAssignments,
        availableGroups,
        everbridgeGroupData,
        handleMapEverbridgeGroups,
    };
};
